import { useState, useMemo, useCallback, useEffect } from 'react'
import { format } from 'date-fns'
import { IntentionalLivingScoreCardV2 } from '@/components/intentional-living-score-card-v2'
import { useInsightsV2 } from '@/hooks/use-insights-v2'
import { usePlanningWeek } from '@/context/planning-week-context'
import { useAuth } from '@/context/auth-context'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'
import { Card, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { RefreshCw, Trash2 } from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { PlanningCalendar, GOOGLE_CALENDAR_COLORS, type ColorMappings, type EventUpdatePayload, type PlanningEvent } from '@/components/planning-calendar'

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Extracts a valid UUID for source_event_id. Analytics returns composite ids for recurring instances (uuid_timestamp). */
function extractSourceEventUuid(id: string): string | null {
  if (UUID_REGEX.test(id)) return id
  const beforeUnderscore = id.split('_')[0]
  return UUID_REGEX.test(beforeUnderscore) ? beforeUnderscore : null
}

export function PlanningPage() {
  const { currentWeekStart } = usePlanningWeek()
  const { user } = useAuth()
  const { data, loading, refresh, refetch } = useInsightsV2(currentWeekStart, 'planned', true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const isExistingPlan = data?.is_existing_plan ?? false
  const [colorMappings, setColorMappings] = useState<ColorMappings>({})

  // Load color mappings when calendar list changes
  const calendarIds = useMemo(() => {
    if (!data?.events) return [] as string[]
    return [...new Set(data.events.map(e => e.integrations_calendar_id).filter(Boolean))]
  }, [data?.events])

  useEffect(() => {
    if (calendarIds.length === 0) return

    async function loadMappings() {
      const { data: rows, error } = await supabase
        .from('analytics_calendars_mapping')
        .select('integrations_calendar_id, color_id, is_ignored, life_domain_id, label_id, life_domains (title), life_domains_labels (name)')
        .in('integrations_calendar_id', calendarIds)

      if (error || !rows) return

      const result: ColorMappings = {}
      for (const row of rows) {
        const calId = row.integrations_calendar_id as string
        const colorId = row.color_id as string
        const gcColor = GOOGLE_CALENDAR_COLORS[colorId]
        if (!gcColor) continue

        if (!result[calId]) result[calId] = []

        const ldRaw = row.life_domains as unknown
        const lblRaw = row.life_domains_labels as unknown
        const ldData = (Array.isArray(ldRaw) ? ldRaw[0] : ldRaw) as { title: string } | null | undefined
        const lblData = (Array.isArray(lblRaw) ? lblRaw[0] : lblRaw) as { name: string } | null | undefined

        result[calId].push({
          colorId,
          hex: gcColor.hex,
          colorName: gcColor.name,
          isIgnored: row.is_ignored as boolean,
          lifeDomainTitle: ldData?.title ?? null,
          labelName: lblData?.name ?? null,
        })
      }

      setColorMappings(result)
    }

    loadMappings()
  }, [calendarIds])

  const [calendarEvents, setCalendarEvents] = useState<PlanningEvent[]>([])

  useEffect(() => {
    if (!data?.events) return
    setCalendarEvents(data.events.map(e => ({
      id: e.id,
      title: e.event_title || '(No title)',
      start: new Date(e.event_start_time),
      end: new Date(e.event_end_time),
      allDay: false,
      color: e.event_color ?? undefined,
      htmlLink: e.event_html_link ?? undefined,
      integrationsCalendarId: e.integrations_calendar_id,
      eventColorId: e.event_color_id,
    })))
  }, [data?.events])

  const handleRefresh = async () => {
    if (!user?.id) {
      toast.error('User not authenticated')
      return
    }

    setIsRefreshing(true)

    try {
      const result = await refresh()
      const success = result?.success ?? false

      if (success) {
        toast.success('Planning insights refreshed successfully!')
      } else {
        toast.error(result?.error || 'Failed to refresh planning insights')
      }
    } catch (error) {
      console.error('Refresh failed:', error)
      toast.error('Failed to refresh planning insights')
    } finally {
      setIsRefreshing(false)
    }
  }

  const handleSave = async () => {
    if (!user?.id || !data) {
      toast.error('No data to save')
      return
    }

    setIsSaving(true)
    const weekStartStr = format(currentWeekStart, 'yyyy-MM-dd')

    try {
      const { error: deleteError } = await supabase
        .from('events_planned')
        .delete()
        .eq('user_id', user.id)
        .eq('week_start', weekStartStr)

      if (deleteError) throw deleteError

      if (data.events && data.events.length > 0) {
        const rows = data.events.map(e => {
          // source_event_id must be a UUID; analytics returns composite ids for recurring instances (uuid_timestamp)
          const sourceEventId = extractSourceEventUuid(e.id)
          return {
            user_id: user.id,
            week_start: weekStartStr,
            source_event_id: sourceEventId,
            integrations_calendar_id: e.integrations_calendar_id,
            event_id: e.event_id,
            event_title: e.event_title,
            event_start_time: e.event_start_time,
            event_end_time: e.event_end_time,
            event_color: e.event_color,
            event_color_id: e.event_color_id,
            event_html_link: e.event_html_link,
            event_is_blocking: e.event_is_blocking,
          }
        })

        const { error: insertError } = await supabase
          .from('events_planned')
          .insert(rows)

        if (insertError) throw insertError
      }

      toast.success('Plan saved!')
      refetch()
    } catch (error) {
      console.error('Save failed:', error)
      toast.error('Failed to save plan')
    } finally {
      setIsSaving(false)
    }
  }

  const handleEventUpdate = useCallback(async (
    id: string,
    updates: EventUpdatePayload,
  ) => {
    setCalendarEvents(prev => prev.map(ev => {
      if (ev.id !== id) return ev
      const patched = { ...ev }
      if (updates.start) patched.start = updates.start
      if (updates.end) patched.end = updates.end
      if (updates.title !== undefined) patched.title = updates.title
      if (updates.colorHex !== undefined) patched.color = updates.colorHex
      if (updates.colorId !== undefined) patched.eventColorId = updates.colorId
      return patched
    }))

    const patch: Record<string, unknown> = { updated_at: new Date().toISOString() }
    if (updates.start) patch.event_start_time = updates.start.toISOString()
    if (updates.end) patch.event_end_time = updates.end.toISOString()
    if (updates.title !== undefined) patch.event_title = updates.title
    if (updates.colorId !== undefined) patch.event_color_id = updates.colorId
    if (updates.colorHex !== undefined) patch.event_color = updates.colorHex

    const { error } = await supabase
      .from('events_planned')
      .update(patch)
      .eq('id', id)

    if (error) {
      console.error('Event update failed:', error)
      toast.error('Failed to update event')
      return
    }

    refetch()
  }, [refetch])

  const handleEventDelete = useCallback(async (id: string) => {
    setCalendarEvents(prev => prev.filter(ev => ev.id !== id))

    const { error } = await supabase
      .from('events_planned')
      .delete()
      .eq('id', id)

    if (error) {
      console.error('Event delete failed:', error)
      toast.error('Failed to delete event')
      return
    }

    refetch()
  }, [refetch])

  const handleDeletePlan = useCallback(async () => {
    if (!user?.id) {
      toast.error('User not authenticated')
      return
    }

    setIsDeleting(true)
    const weekStartStr = format(currentWeekStart, 'yyyy-MM-dd')

    try {
      const { error } = await supabase
        .from('events_planned')
        .delete()
        .eq('user_id', user.id)
        .eq('week_start', weekStartStr)

      if (error) throw error

      toast.success('Plan deleted')
      setCalendarEvents([])
      refetch()
    } catch (error) {
      console.error('Delete plan failed:', error)
      toast.error('Failed to delete plan')
    } finally {
      setIsDeleting(false)
    }
  }, [user?.id, currentWeekStart, refetch])

  const insightsContent = (
    <IntentionalLivingScoreCardV2
      data={data}
      intentionalLivingData={data}
      loading={loading}
      className="mb-8 md:mb-0"
      onRefresh={handleRefresh}
      isRefreshing={isRefreshing}
      showRefreshButton={false}
    />
  )

  return (
    <div className="main-content max-w-none h-full @container flex flex-col">
      <Card className="mb-4 shrink-0">
        <CardHeader className="flex flex-row items-center justify-end space-y-0 gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing || loading}
            className="gap-1.5"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          {!isExistingPlan && (
            <Button
              size="sm"
              onClick={handleSave}
              disabled={isSaving || loading || !data}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          )}
          {isExistingPlan && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isDeleting || loading}
                  className="gap-1.5 bg-background border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                  {isDeleting ? 'Deleting...' : 'Delete Plan'}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this week's plan?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently remove all planned events for this week. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDeletePlan}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </CardHeader>
      </Card>

      {/* Tabbed navigation when content area is narrow (mobile, or desktop with sidebar/small window) */}
      <div className="min-h-0 flex-1 flex flex-col @[64rem]:hidden">
        <Tabs defaultValue="events" className="w-full flex flex-col min-h-0 flex-1">
          <TabsList className="grid w-full grid-cols-2 shrink-0">
            <TabsTrigger value="events">Events</TabsTrigger>
            <TabsTrigger value="insights">Insights</TabsTrigger>
          </TabsList>
          <TabsContent value="events" className="mt-4 min-h-0 flex-1">
            <PlanningCalendar
              events={calendarEvents}
              loading={loading}
              editable={isExistingPlan}
              colorMappings={colorMappings}
              onEventUpdate={handleEventUpdate}
              onEventDelete={handleEventDelete}
            />
          </TabsContent>
          <TabsContent value="insights" className="mt-4 min-h-0 flex-1 overflow-y-auto">
            {insightsContent}
          </TabsContent>
        </Tabs>
      </div>

      {/* Side-by-side when content area is wide enough (≥1024px) */}
      <div className="hidden @[64rem]:grid @[64rem]:grid-cols-4 @[64rem]:gap-6 min-h-0 flex-1">
        <div className="@[64rem]:col-span-3 min-h-0">
          <PlanningCalendar
            events={calendarEvents}
            loading={loading}
            editable={isExistingPlan}
            colorMappings={colorMappings}
            onEventUpdate={handleEventUpdate}
            onEventDelete={handleEventDelete}
          />
        </div>
        <div className="@[64rem]:col-span-1 overflow-y-auto">
          {insightsContent}
        </div>
      </div>
    </div>
  )
}
